import { Fade, Grid, Link, Stack } from "@mui/material";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { ArrowLeft, ArrowRight } from "akar-icons";
import React, { useCallback, useState } from "react";

import { SubscriptionManagementCard } from "./libs/components/SubscriptionManagementCard";
import { IV2SubscriptionManagement } from "./libs/types";
import { ErrorBoundary } from "../../../../components/ErrorBoundary";
import { useSmartrrTheme } from "../../../../hooks/useSmartrrTheme";
import { resetScroll } from "../../../../utils";
import { usePurchasablesForAdding } from "../../../hooks/usePurchasablesForAdding";
import { useFirstPurchaseStateToDisplay } from "../../../hooks/usePurchaseStates";
import { AddonListCard } from "../V2SubscriptionManagement/libs/components/AddonListCard";

export const V2SubscriptionManagement = ({ upcomingDelivery }: IV2SubscriptionManagement) => {
  const [showAllSubscriptions, setShowAllSubscriptions] = useState<boolean>(false);

  const {
    delivery: [firstDate, firstPurchaseStates],
    sortedPurchaseStates,
  } = useFirstPurchaseStateToDisplay(upcomingDelivery!);

  const theme = useSmartrrTheme();

  const purchasables = usePurchasablesForAdding(upcomingDelivery);

  let trimmedPurchaseStates: [Date, IPurchaseState[]][] = [];

  if (firstPurchaseStates.length > 1) {
    trimmedPurchaseStates = [
      [firstDate, firstPurchaseStates.slice(1, firstPurchaseStates.length)],
      ...sortedPurchaseStates.slice(1, sortedPurchaseStates.length),
    ];
  } else {
    const indexToRemove = upcomingDelivery
      ? sortedPurchaseStates.findIndex(([_, purchaseStates]) =>
          purchaseStates.some(cps => cps.id === upcomingDelivery.purchaseState.id)
        )
      : 0;
    trimmedPurchaseStates = sortedPurchaseStates.filter((_, i) => i !== indexToRemove);
  }

  const onSwapViewState = useCallback(() => {
    resetScroll();
    setShowAllSubscriptions(prev => !prev);
  }, []);

  return (
    <ErrorBoundary>
      <Stack gap="20px">
        <Grid container spacing="20px">
          <Grid item xs={12} md={6}>
            <SubscriptionManagementCard
              purchaseState={firstPurchaseStates[0]}
              showAllSubscriptions={showAllSubscriptions}
            />
          </Grid>

          {showAllSubscriptions && trimmedPurchaseStates.length ? (
            trimmedPurchaseStates.map(group =>
              group[1].length ? (
                group[1].map(purchaseState =>
                  purchaseState ? (
                    <Grid item xs={12} md={6} key={purchaseState.id}>
                      <Fade in={showAllSubscriptions}>
                        <div>
                          <SubscriptionManagementCard
                            purchaseState={purchaseState}
                            showAllSubscriptions={showAllSubscriptions}
                          />
                        </div>
                      </Fade>
                    </Grid>
                  ) : (
                    <React.Fragment />
                  )
                )
              ) : (
                <React.Fragment key={group[0].toString()} /> // Added key prop to React.Fragment
              )
            )
          ) : upcomingDelivery ? (
            <Grid item xs={12} md={6}>
              <Fade in={!showAllSubscriptions}>
                <div style={{ height: "100%" }}>
                  <AddonListCard purchasables={purchasables} delivery={upcomingDelivery} />
                </div>
              </Fade>
            </Grid>
          ) : (
            <React.Fragment />
          )}
        </Grid>

        {trimmedPurchaseStates.length ? (
          <Stack
            direction="row"
            gap="7px"
            onClick={onSwapViewState}
            sx={{
              width: "max-content",
              a: {
                cursor: "pointer",
                color: theme.modernBodyFontColor,
              },
            }}
          >
            {showAllSubscriptions ? (
              <React.Fragment>
                <ArrowLeft strokeWidth={2} size={18} />
                <Link variant="body1" underline="always">
                  {typedLocalize("general.goBackBtn")}
                </Link>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <ArrowRight strokeWidth={2} size={18} />
                <Link variant="body1" underline="always">
                  {typedLocalize("subscriptions.viewSubscriptions")}
                </Link>
              </React.Fragment>
            )}
          </Stack>
        ) : null}
      </Stack>
    </ErrorBoundary>
  );
};
